import { useUserContext } from '@/app/context/UserContext';
import { useCompanyContext } from '@/app/context/CompanyContext';
import { useColorMode, useConfirmDialog } from '@/hooks';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider, Avatar, Chip, Switch } from '@mui/material';
import { useParams, useRouter } from 'next/navigation';
import { TbArrowsLeftRight, TbLogout, TbMoon, TbUser } from 'react-icons/tb';
import clsx from 'clsx';
import { MenuGrowTransition } from '@/components/ui';
import { useCallback, useMemo, useRef, useState } from 'react';
const ARROW_SIZE = 12;
/** Keep the arrow clear of the menu's rounded corners. */
const ARROW_EDGE_INSET = 20;
/** The paper's `border` class. */
const MENU_BORDER_WIDTH = 1;
export const AppLayoutUserMenu = ({
  anchorEl,
  open,
  onClose
}) => {
  const {
    confirm
  } = useConfirmDialog();
  const {
    clearUser,
    user
  } = useUserContext();
  const {
    company,
    companyConfig
  } = useCompanyContext();
  const {
    companySlug
  } = useParams();
  const router = useRouter();
  const {
    colorMode,
    setColorMode
  } = useColorMode();
  const isDarkModeEnabled = companyConfig?.isDarkModeEnabled === true;
  const companyLogoUrl = useMemo(() => {
    if (companyConfig?.showCompanyLogo) {
      if (companyConfig?.companyLogoUrl) {
        return companyConfig.companyLogoUrl;
      }
      return companySlug ? `/images/logo/${companySlug}.png` : '';
    }
    return '';
  }, [companyConfig, companySlug]);
  const handleConfirmLogout = async e => {
    e?.preventDefault();
    try {
      clearUser();

      // Small delay to ensure state updates are processed
      setTimeout(() => {
        window.location.replace('/auth/logout?returnTo=' + new URL('/api/auth/logout-callback', window.location.origin).toString());
      }, 100);
    } catch (error) {
      console.error('Logout error', error);
    }
  };
  const handleLogout = async () => {
    confirm({
      title: 'Sign Out',
      content: 'Are you sure you want to sign out?',
      color: 'error',
      onConfirm: () => handleConfirmLogout()
    });
  };
  const handleToggleColorMode = () => {
    setColorMode(colorMode === 'dark' ? 'light' : 'dark');
  };

  // Avatar centre, arrow and Logout icon share one line: the popover pins the Logout row's centre (a pixel
  // transform origin, measured from the menu's top) to the avatar's vertical centre; the arrow sits there and
  // the menu grows from it. Measured inside the border (the arrow's coordinates) — the origin adds it back.
  // `offsetTop` ignores the grow transform. The rows have fixed heights, so measuring on enter is enough.
  const logoutItemRef = useRef(null);
  const [logoutCenterY, setLogoutCenterY] = useState(null);
  const measureLogoutCenter = useCallback(() => {
    const item = logoutItemRef.current;
    const list = item?.offsetParent;
    if (!item || !list) return;
    setLogoutCenterY(list.offsetTop + item.offsetTop + item.offsetHeight / 2);
  }, []);
  const arrowTop = logoutCenterY === null ? null : Math.max(logoutCenterY, ARROW_EDGE_INSET);
  return <Menu id="user-menu" anchorEl={anchorEl} open={open} onClose={onClose} anchorOrigin={{
    vertical: 'center',
    horizontal: 'right'
  }} transformOrigin={{
    vertical: logoutCenterY === null ? 'bottom' : logoutCenterY + MENU_BORDER_WIDTH,
    horizontal: 'left'
  }}
  // Tighter than the default 16px so the menu isn't nudged up off the avatar's line at the bottom of the screen.
  marginThreshold={8} slots={{
    transition: MenuGrowTransition
  }} slotProps={{
    transition: {
      onEntering: measureLogoutCenter
    },
    paper: {
      // mt-0: the theme's MuiMenu paper `marginTop: 4px` lands after positioning and would shift the menu (and its origin) off the avatar's line.
      className: 'w-72! max-w-80! rounded-xl! border border-[var(--border)]! shadow-lg! ml-3! mt-0! overflow-visible!',
      sx: {
        // Arrow pointing back at the avatar button: a rotated square on the left edge.
        '&::before': {
          content: '""',
          display: arrowTop === null ? 'none' : 'block',
          position: 'absolute',
          left: -ARROW_SIZE / 2 - 1,
          top: (arrowTop ?? 0) - ARROW_SIZE / 2,
          width: ARROW_SIZE,
          height: ARROW_SIZE,
          backgroundColor: 'inherit',
          borderLeft: '1px solid var(--border)',
          borderBottom: '1px solid var(--border)',
          transform: 'rotate(45deg)'
        }
      }
    }
  }} MenuListProps={{
    className: 'py-0!'
  }}>
            <div className="flex flex-col items-center px-4 pt-6 text-center">
                <Avatar src={user?.picture || ''} alt={user?.name || 'John Doe'} className="size-16! shrink-0!" />
                <div className="mt-3 flex w-full min-w-0 flex-col items-center">
                    <p className="w-full truncate text-base font-semibold text-foreground">{user?.name || 'John Doe'}</p>
                    <p className="w-full truncate text-sm text-foreground-muted">{user?.email || 'john.doe@example.com'}</p>
                </div>
            </div>
            {company?.companyName && <div className="py-3 px-4 flex items-center justify-center gap-2">
                    <Chip avatar={companyLogoUrl ? <Avatar src={companyLogoUrl} alt={company.companyName || ''} className="size-4! border! border-border!" slotProps={{
        img: {
          className: 'object-contain!'
        }
      }} /> : undefined} label={company.companyName} size="x-small" color="brand" variant="soft" className="max-w-full! [&_.MuiChip-label]:truncate! font-medium!" />
                </div>}

            <Divider />

            <p className="pt-5 px-4 pt-3 pb-0.5 text-[11px] font-medium uppercase tracking-wide text-foreground-muted">Account</p>

            <MenuItem className="mx-1! rounded-lg! px-3! py-2.5!" onClick={() => {
      onClose();
      router.push(`/${companySlug}/profile`);
    }}>
                <ListItemIcon className="min-w-9!">
                    <TbUser className="size-5" />
                </ListItemIcon>
                <ListItemText primary="Profile" primaryTypographyProps={{
        className: 'text-sm! font-medium!'
      }} />
            </MenuItem>

            {isDarkModeEnabled && <MenuItem className="mx-1! rounded-lg! px-3! py-2.5!" onClick={handleToggleColorMode}>
                    <ListItemIcon className="min-w-9!">
                        <TbMoon className="size-5" />
                    </ListItemIcon>
                    <ListItemText primary={<span className="inline-flex items-center gap-1.5">
                                Dark mode
                                <Chip label="Beta" size="x-small" variant="soft" color="brand" className="font-medium!" />
                            </span>} primaryTypographyProps={{
        className: 'text-sm! font-medium!',
        component: 'div'
      }} />
                    <Switch size="small" edge="end" checked={colorMode === 'dark'} onClick={e => e.stopPropagation()} onChange={(_, checked) => setColorMode(checked ? 'dark' : 'light')} className="mr-0.5!" />
                </MenuItem>}

            {Number(user?.linkedCompaniesCount || 0) > 1 && <MenuItem className="mx-1! rounded-lg! px-3! py-2.5!" onClick={() => {
      onClose();
      window.location.href = '/api/auth/switch-company';
    }}>
                    <ListItemIcon className="min-w-9!">
                        <TbArrowsLeftRight className="size-5" />
                    </ListItemIcon>
                    <ListItemText primary="Switch Company" primaryTypographyProps={{
        className: 'text-sm! font-medium!'
      }} />
                </MenuItem>}

            <Divider className="my-2!" />

            <MenuItem ref={logoutItemRef} className={clsx('mx-1! my-1! rounded-lg! px-3! py-2.5!', 'text-[var(--status-error)]!')} onClick={() => {
      onClose();
      handleLogout();
    }}>
                <ListItemIcon className="min-w-9! text-inherit!">
                    <TbLogout className="size-5" />
                </ListItemIcon>
                <ListItemText primary="Logout" primaryTypographyProps={{
        className: 'text-sm! font-medium! text-inherit!'
      }} />
            </MenuItem>
        </Menu>;
};